import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  Users,
  Activity,
  DollarSign,
  Clock,
  Plus,
  ArrowRight,
  UserPlus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";

const CHART_COLORS = ["hsl(153, 40%, 30%)", "hsl(214, 20%, 75%)"];

const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  confirmed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  completed: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  cancelled: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
  no_show: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
};

const STATUS_LABELS = {
  scheduled: "Programada",
  confirmed: "Confirmada",
  completed: "Completada",
  cancelled: "Cancelada",
  no_show: "No asistio",
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    dashboardAPI.get().then((res) => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    dashboardAPI.charts().then((res) => setChartData(res.data)).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
        <div className="h-64 rounded-xl bg-muted animate-pulse" />
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Resumen del dia &middot; {new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() => navigate("/pacientes")}
            data-testid="dashboard-new-patient-btn"
          >
            <UserPlus className="w-4 h-4 mr-1.5" />
            Nuevo paciente
          </Button>
          <Button
            size="sm"
            className="rounded-full"
            onClick={() => navigate("/agenda")}
            data-testid="dashboard-new-appt-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Nueva cita
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Citas hoy", value: stats.appointments_today || 0, icon: CalendarDays, color: "text-primary" },
          { label: "Pacientes activos", value: stats.active_patients || 0, icon: Users, color: "text-secondary" },
          { label: "Episodios activos", value: stats.active_episodes || 0, icon: Activity, color: "text-emerald-600 dark:text-emerald-400" },
          { label: "Ingresos del mes", value: `$${(stats.monthly_revenue || 0).toLocaleString("es-MX")}`, icon: DollarSign, color: "text-amber-600 dark:text-amber-400" },
        ].map((s, i) => (
          <Card key={s.label} className={`stat-card animate-slide-up stagger-${i+1}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                  <p className="text-2xl font-bold mt-1 text-foreground">{s.value}</p>
                </div>
                <div className={`p-2.5 rounded-xl bg-muted ${s.color}`}>
                  <s.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      {chartData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up stagger-2">
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Citas ultimos 14 dias</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.daily_appointments}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="citas" fill="hsl(153, 40%, 30%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Episodios</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-center">
              {chartData.episodes_by_status?.some((e) => e.value > 0) ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={chartData.episodes_by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={4} label={({ name, value }) => `${name}: ${value}`} style={{ fontSize: 11 }}>
                      {chartData.episodes_by_status.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-xs text-muted-foreground py-8">Sin datos de episodios</p>
              )}
            </CardContent>
          </Card>
          <Card className="lg:col-span-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Ingresos mensuales</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData.monthly_income}>
                  <defs>
                    <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(12, 76%, 61%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(12, 76%, 61%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [`$${v.toLocaleString("es-MX")}`, "Ingresos"]} />
                  <Area type="monotone" dataKey="ingresos" stroke="hsl(12, 76%, 61%)" fill="url(#incomeGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Appointments */}
        <Card className="lg:col-span-2 animate-slide-up stagger-3">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Citas de hoy</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/agenda")} data-testid="view-agenda-btn">
                Ver agenda <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {data?.today_appointments?.length > 0 ? (
              <div className="space-y-2.5">
                {data.today_appointments.map((appt) => (
                  <div
                    key={appt.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => appt.patient_id && navigate(`/pacientes/${appt.patient_id}`)}
                    data-testid={`appt-${appt.id}`}
                  >
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground w-20 flex-shrink-0">
                      <Clock className="w-3.5 h-3.5" />
                      {appt.start_time}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{appt.patient_name}</p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={`text-[10px] ${STATUS_COLORS[appt.status] || ""}`}
                    >
                      {STATUS_LABELS[appt.status] || appt.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-muted-foreground">
                <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No hay citas programadas para hoy</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate("/agenda")}
                  className="mt-2"
                  data-testid="create-first-appt-btn"
                >
                  Agendar primera cita
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Episodes */}
        <Card className="animate-slide-up stagger-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Episodios activos</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.recent_episodes?.length > 0 ? (
              <div className="space-y-2.5">
                {data.recent_episodes.map((ep) => (
                  <div
                    key={ep.id}
                    className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                    onClick={() => navigate(`/episodios/${ep.id}`)}
                    data-testid={`episode-${ep.id}`}
                  >
                    <p className="text-sm font-medium text-foreground truncate">{ep.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{ep.patient_name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Sin episodios activos</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
